import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import usersApi from '@/api/usersApi';
import type { ApiResponse, UserClientModel } from '@/api/types';

type UserPayload = Record<string, unknown>;
type UserResponse = ApiResponse<UserClientModel>;
type UserListResponse = ApiResponse<UserClientModel[]>;

// Query keys for user operations
export const USERS_KEYS = {
  all: ['users'] as const,
  me: ['users', 'me'] as const,
  byId: (id: string) => ['users', id] as const,
};

/**
 * Hook to get current user profile (me)
 */
export function useUserMe(options: ApiQueryOptions<UserResponse> = {}) {
  return useAuthenticatedQuery<UserResponse>(
    USERS_KEYS.me,
    token => usersApi.getMe(token),
    options,
  );
}

/**
 * Hook to get all users
 */
export function useAllUsers(options: ApiQueryOptions<UserListResponse> = {}) {
  return useAuthenticatedQuery<UserListResponse>(
    USERS_KEYS.all,
    token => usersApi.getAllUsers(token),
    options,
  );
}

/**
 * Hook to get user by ID
 */
export function useUserById(
  id: string,
  options: ApiQueryOptions<UserResponse> = {},
) {
  return useAuthenticatedQuery<UserResponse>(
    USERS_KEYS.byId(id),
    token => usersApi.getUserById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook to update current user profile (me)
 */
export function useUpdateUserMe(
  options: ApiMutationOptions<UserResponse, { profileData: UserPayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<UserResponse, { profileData: UserPayload }>(
    ({ profileData, token }) => usersApi.updateMe(profileData, token),
    {
      onSuccess: () => {
        // Invalidate user me query to refetch updated profile
        queryClient.invalidateQueries({ queryKey: USERS_KEYS.me });
      },
      ...options,
    },
  );
}

/**
 * Hook to create new user
 */
export function useCreateUser(
  options: ApiMutationOptions<unknown, { userData: UserPayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<unknown, { userData: UserPayload }>(
    ({ userData, token }) => usersApi.createUser(userData, token),
    {
      onSuccess: () => {
        // Invalidate users list to show new user
        queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook to update user
 */
export function useUpdateUser(
  options: ApiMutationOptions<
    UserResponse,
    { id: string; userData: UserPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    UserResponse,
    { id: string; userData: UserPayload }
  >(({ id, userData, token }) => usersApi.updateUser(id, userData, token), {
    onSuccess: (_data, variables) => {
      // Invalidate specific user and list
      queryClient.invalidateQueries({
        queryKey: USERS_KEYS.byId(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
    },
    ...options,
  });
}

/**
 * Hook to delete user
 */
export function useDeleteUser(
  options: ApiMutationOptions<unknown, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<unknown, { id: string }>(
    ({ id, token }) => usersApi.deleteUser(id, token),
    {
      onSuccess: (_data, variables) => {
        // Remove from cache and invalidate list
        queryClient.removeQueries({ queryKey: USERS_KEYS.byId(variables.id) });
        queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook to update user status (active/inactive)
 */
export function useUpdateUserStatus(
  options: ApiMutationOptions<unknown, { id: string; status: unknown }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<unknown, { id: string; status: unknown }>(
    ({ id, status, token }) => usersApi.updateUserStatus(id, status, token),
    {
      onSuccess: (_data, variables) => {
        // Invalidate specific user and list
        queryClient.invalidateQueries({
          queryKey: USERS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook to update user profile by ID (admin operation)
 */
export function useUpdateUserProfile(
  options: ApiMutationOptions<
    unknown,
    { id: string; profileData: UserPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    unknown,
    { id: string; profileData: UserPayload }
  >(
    ({ id, profileData, token }) =>
      usersApi.updateUserProfile(id, profileData, token),
    {
      onSuccess: (_data, variables) => {
        // Invalidate specific user and list
        queryClient.invalidateQueries({
          queryKey: USERS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: USERS_KEYS.all });
      },
      ...options,
    },
  );
}
